<template>
    <div class="ah-page financial-analysis">
        <div class="page-header bottom-margin">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title">
                        <h1>財務資產收支分析</h1>
                    </div>
                    <div class="actions"></div>
                </div>
            </div>
        </div>
        <div class="ah-container">
            <div class="ah-panel">
                <div class="panel-body">
                    <div class="year-selector">
                        <div class="label">報表年度：</div>
                        <el-select v-model="year" placeholder="年度" size="small" no-data-text="無數據" style="vertical-align: top; width: 120px" @change="updateData">
                            <el-option v-for="item in yearOptions" :key="item" :label="item" :value="item" />
                        </el-select>
                    </div>
                    <div class="chart">
                        <v-chart ref="barchart" key="chart" :option="groupBarChartData" style="width: 100%" :style="{ height: '250px' }" autoresize />
                    </div>
                    <div class="table-actions">
                        <div>
                            <el-button size="small" @click="openAll">全部展開</el-button>
                            <el-button size="small" @click="closeAll">全部收闔</el-button>
                        </div>
                        <div>
                            <el-checkbox-group v-model="columnDisplayList">
                                <el-checkbox label="期初金額"></el-checkbox>
                                <el-checkbox label="本期收入"></el-checkbox>
                                <el-checkbox label="本期支出"></el-checkbox>
                                <el-checkbox label="本期增減"></el-checkbox>
                            </el-checkbox-group>
                        </div>
                    </div>

                    <el-table :data="tableData" ref="table" border class="ah-analysis-table" v-loading="isLoading">
                        <el-table-column type="expand" width="25" fixed class-name="expand">
                            <template slot-scope="scope">
                                <SubTable
                                    :data="scope.row.children"
                                    :group-no="scope.row.groupNo"
                                    :column-display-list="columnDisplayList"
                                    @item-click="onSubItemClick"
                                    @toggleLoading="isLoading = !isLoading"
                                />
                            </template>
                        </el-table-column>
                        <el-table-column prop="name" label="項目" fixed width="265" class-name="header">
                            <template slot-scope="scope">
                                <span>
                                    <span>{{ scope.row.name }}</span>
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column key="1" prop="openingBalance" label="期初金額" align="right" v-if="columnDisplayList.includes('期初金額')">
                            <template slot-scope="scope">
                                <Money :v="scope.row.openingBalance" zero="-" round />
                            </template>
                        </el-table-column>
                        <el-table-column key="2" prop="operatingRevenue" label="本期收入" align="right" v-if="columnDisplayList.includes('本期收入')">
                            <template slot-scope="scope">
                                <Money :v="scope.row.operatingRevenue" zero="-" round />
                            </template>
                        </el-table-column>
                        <el-table-column key="3" prop="operatingCosts" label="本期支出" align="right" v-if="columnDisplayList.includes('本期支出')">
                            <template slot-scope="scope">
                                <Money :v="scope.row.operatingCosts" zero="-" round />
                            </template>
                        </el-table-column>
                        <el-table-column key="4" prop="netIncome" label="本期增減" align="right" v-if="columnDisplayList.includes('本期增減')">
                            <template slot-scope="scope">
                                <Money
                                    :v="scope.row.netIncome"
                                    zero="-"
                                    round
                                    :showPositivePlus="true"
                                    :class="{
                                        increase: scope.row.netIncome > 0,
                                        decrease: scope.row.netIncome < 0
                                    }"
                                />
                            </template>
                        </el-table-column>
                        <el-table-column key="5" prop="totalAmount" label="本期餘額" align="right">
                            <template slot-scope="scope">
                                <Money :v="scope.row.totalAmount" zero="-" round />
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </div>
        <ItemsAnalysisDialog
            :visible="subItemDialog.visible"
            :sno="subItemDialog.itemSno"
            :year="year"
            @close="subItemDialog.visible = false"
        />
    </div>
</template>
<script>
import moment from 'moment';
import _ from 'lodash';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart } from 'echarts/charts';
import {
    TitleComponent, TooltipComponent, LegendComponent, GridComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import 'echarts/lib/component/legend';
import { financeStatementList } from '@/assets/commonData/options';
import SubTable from './SubTable.vue';
import ItemsAnalysisDialog from './ItemsAnalysisDialog.vue';

use([CanvasRenderer, PieChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent]);
export default {
    name: 'AnalysisPayout',
    components: {
        VChart,
        SubTable,
        ItemsAnalysisDialog,
    },
    data() {
        return {
            year: null,
            yearOptions: [],
            columnDisplayList: ['期初金額', '本期收入', '本期支出', '本期增減'],
            groupBarChartData: {
                legend: { orient: 'vertical', right: 100 },
                tooltip: { trigger: 'axis' },
                color: ['#1ED700', '#FF859B'],
                xAxis: { data: [] },
                yAxis: {},
                series: [],
            },

            isLoading: false,
            tableData: [],
            subItemDialog: {
                visible: false,
                itemSno: null,
            },
        };
    },
    computed: {
        chartHeight() {
            return `${this.groupBarChartData.series[0].data.length * 50 + 100}px`;
        },
    },
    created() {
        this.init();
    },
    methods: {
        async init() {
            this.initChartData();
            await this.fetchYearOptions();
            await this.updateData();
        },
        initChartData() {
            const settings = {
                type: 'bar',
                barWidth: '20%',
                data: [0, 0, 0, 0, 0],
            };
            this.groupBarChartData.xAxis.data = _.map(financeStatementList, (o) => `${o.id}. ${o.value}`);
            this.groupBarChartData.series = [
                { name: '財務收入', ...settings },
                { name: '財務支出', ...settings },
            ];
            this.groupBarChartData.legend.data = ['財務收入', '財務支出'];
        },
        async fetchYearOptions() {
            const yearOptions = [];
            const START = 2020;
            const END = moment().year();

            for (let i = END; i >= START; i -= 1) {
                yearOptions.push(i);
            }

            if (yearOptions && yearOptions.length > 0) {
                this.yearOptions = yearOptions;
            } else {
                this.yearOptions = [new Date().getFullYear()];
            }

            this.year = _.head(this.yearOptions);
        },
        async updateData() {
            const resp = await this.$api({
                url: 'FinancialStatement/GetReport',
                method: 'GET',
                params: { year: this.year },
            });

            this.fetchTableData(resp.data.groups);
            this.fetchChartData(resp.data.groups);
        },
        fetchTableData(data) {
            this.tableData = _.map(data, (o) => ({
                ...o,
                id: o.groupNo,
                name: `${o.groupNo}. ${_.find(financeStatementList, ['id', o.groupNo]).value}`,
                children: _.map(o.items, (c) => ({
                    ...c,
                    id: c.sno,
                    name: c.itemName,
                })),
            }));
        },
        fetchChartData(data) {
            const operatingRevenue = _.map(data, (o) => Math.round(o.operatingRevenue));
            const operatingCosts = _.map(data, (o) => Math.round(o.operatingCosts));

            const incomeChart = _.find(this.groupBarChartData.series, ['name', '財務收入']);
            const costChart = _.find(this.groupBarChartData.series, ['name', '財務支出']);

            incomeChart.data = [...operatingRevenue];
            costChart.data = [...operatingCosts];
        },
        openAll() {
            this.tableData.forEach((item) => {
                this.$refs.table.toggleRowExpansion(item, true);
            });
        },
        closeAll() {
            this.tableData.forEach((item) => {
                this.$refs.table.toggleRowExpansion(item, false);
            });
        },
        onSubItemClick(itemSno) {
            this.subItemDialog.visible = true;
            this.subItemDialog.itemSno = itemSno;
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.financial-analysis {
    .year-selector {
        display: flex;
        align-items: center;
        .label {
            margin-right: 30px;
            font-size: 10pt;
            color: #888;
        }
    }
    .chart {
        border: 1px solid #cccccc;
        padding-top: 30px;
        margin: 20px 0 40px;
    }
    th.header {
        text-align: center;
    }
    .money {
        display: block;
        white-space: nowrap;
        word-break: keep-all;
        text-overflow: unset;

        &.increase {
            color: green;
        }

        &.decrease {
            color: red;
        }
    }

    .table-actions {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin: 0 0 10px 0;
    }

    .ah-analysis-table {
        margin: 0 0 50px 0;

        .el-table__row--level-0 {
            td {
                padding: 15px 0;
                font-weight: bold;
            }

            &.hover-row {
                td {
                    background-color: #eaeff8;
                }
            }
        }
        .el-table__expanded-cell {
            padding: 0;
        }
        .expand {
            border-right: 0px;
        }
    }
}
</style>
